/**
 * 報價／合約項目金額計算
 *
 * 倍率計價（單位 `%` 或歷史單位「實報實銷」）：
 * - 儲存值 50、100、110 代表百分比；歷史「實報實銷」單價 1、1.1 代表 100%、110%
 * - 儲存的報價數量 = 實際採購金額（元）
 * - 應收金額 = 實際金額 × (百分比 / 100)
 *   例：50% → 收一半；100% → 實報實銷；110% → 含 10% 服務費
 * - 客戶端顯示（PDF／預覽）：數量固定為 1（單位「式」）；
 *   採購無牌價，合約價為實際採購金額 × 收費比例。見 getQuoteLineDisplay。
 *
 * 固定單價（小時、計次等）：金額 = 單價 × 數量；合約價不得高於牌價。
 */

export function isMarkupRateUnit(unit?: string | null): boolean {
  const u = (unit || '').trim().replace(/％/g, '%');
  if (!u) return false;
  if (u === '%' || u === '實報實銷' || u === '百分比') return true;
  return u.includes('%');
}

export function isMarkupRate(params: {
  unit?: string | null;
  chargeType?: string | null;
}): boolean {
  if ((params.chargeType || '').trim() === 'percentage') {
    return true;
  }
  return isMarkupRateUnit(params.unit);
}

/** 歷史「實報實銷」單價若 ≤ 10 視為倍率（1 = 100%）；否則視為已是百分比數字 */
function inferMarkupUnitForStored(stored: number, unit?: string | null): string {
  if (isMarkupRateUnit(unit)) {
    return (unit || '%').trim();
  }
  return stored > 10 ? '%' : '實報實銷';
}

/** 儲存值 → 百分比數字（50、100、110） */
export function storedRateToPercent(stored: number, unit?: string | null): number {
  const n = Number(stored) || 0;
  const u = (unit || '').trim();
  if (u === '%') {
    return n;
  }
  if (u === '實報實銷') {
    if (n > 10) {
      return n;
    }
    return n * 100;
  }
  return n;
}

/** 儲存值 → 金額倍率（1 = 100%） */
export function storedRateToFactor(stored: number, unit?: string | null): number {
  if (isMarkupRateUnit(unit)) {
    return storedRateToPercent(stored, unit) / 100;
  }
  return Number(stored) || 0;
}

export function quantityUnitLabel(unit?: string | null): string {
  if (isMarkupRateUnit(unit)) {
    return '元';
  }
  return (unit || '小時').trim() || '小時';
}

/** 客戶端報價顯示：倍率項目數量固定為 1 */
export const MARKUP_DISPLAY_QUANTITY = 1;

/** 客戶端報價顯示：倍率項目數量單位（非工時、非採購金額） */
export const MARKUP_DISPLAY_UNIT = '式';

/** 採購項目不走服務收費表，僅這兩種收費類別 */
export const PROCUREMENT_SERVICE_TYPES = ['境外採購（美金）', '境內採購（台幣）'] as const;

export type ProcurementServiceType = (typeof PROCUREMENT_SERVICE_TYPES)[number];

/** 新增採購列時的預設收費比例（100%＝實報實銷）；list_price 與收費比例相同（無獨立牌價） */
export const PROCUREMENT_DEFAULT_CHARGE_PERCENT = 100;

export function isProcurementServiceType(serviceType?: string | null): boolean {
  const name = (serviceType || '').trim();
  return (PROCUREMENT_SERVICE_TYPES as readonly string[]).includes(name);
}

/** 採購／倍率列：收費類別為採購，或單位為 %／實報實銷 */
export function isProcurementLine(item: { service_type?: string | null; unit?: string | null }): boolean {
  return isProcurementServiceType(item.service_type) || isMarkupRateUnit(item.unit);
}

export interface QuoteItemAmountInput {
  service_type?: string | null;
  unit?: string | null;
  unit_price?: number;
  list_price?: number;
  contract_price?: number;
  quantity?: number;
  is_deduction?: boolean;
}

export function quoteItemLineAmounts(item: QuoteItemAmountInput): LineAmounts {
  const procurement = isProcurementLine(item);
  const contractPrice = Number(item.contract_price) || 0;
  return calculateLineAmounts({
    listPrice: procurement ? contractPrice : (Number(item.unit_price ?? item.list_price) || 0),
    contractPrice,
    quantity: Number(item.quantity) || 0,
    unit: procurement ? (item.unit || '%') : item.unit,
    forceMarkup: procurement,
    isDeduction: item.is_deduction
  });
}

export interface QuoteAmountSummary {
  hoursAfterDiscount: number;
  procurementAfterDiscount: number;
  subtotal: number;
  discount: number;
  afterDiscount: number;
  tax: number;
  total: number;
}

/** 工時類別折扣後 + 採購類別折扣後 = 折扣後總金額；含稅總金額再加 5% 稅 */
export function summarizeQuoteItemTotals(
  items: QuoteItemAmountInput[],
  taxRate = 5
): QuoteAmountSummary {
  let hoursAfterDiscount = 0;
  let procurementAfterDiscount = 0;
  let subtotal = 0;
  let discount = 0;

  for (const item of items) {
    if (!item.service_type) continue;
    const amounts = quoteItemLineAmounts(item);
    subtotal += amounts.subtotal;
    discount += amounts.subtotal_discount;
    if (isProcurementLine(item)) {
      procurementAfterDiscount += amounts.subtotal_afterdiscount;
    } else {
      hoursAfterDiscount += amounts.subtotal_afterdiscount;
    }
  }

  const afterDiscount = hoursAfterDiscount + procurementAfterDiscount;
  const tax = Math.round(afterDiscount * (taxRate / 100));
  const total = Math.round(afterDiscount + tax);
  return {
    hoursAfterDiscount,
    procurementAfterDiscount,
    subtotal,
    discount,
    afterDiscount,
    tax,
    total
  };
}

export function applyQuantity(rate: number, quantity: number, unit?: string | null, asMarkup?: boolean): number {
  const qty = Number(quantity) || 0;
  if (asMarkup || isMarkupRateUnit(unit)) {
    const percentUnit = isMarkupRateUnit(unit) ? unit : '%';
    return (qty * storedRateToPercent(rate, percentUnit)) / 100;
  }
  return qty * (Number(rate) || 0);
}

/** 採購合約價 = 實際採購金額 × 收費比例／100 */
export function purchaseContractAmount(purchaseAmount: number, chargePercent: number): number {
  return ((Number(purchaseAmount) || 0) * (Number(chargePercent) || 0)) / 100;
}

export interface LineAmountInput {
  listPrice: number;
  contractPrice: number;
  quantity: number;
  unit?: string | null;
  chargeType?: string | null;
  isDeduction?: boolean;
  forceMarkup?: boolean;
}

export interface LineAmounts {
  subtotal: number;
  subtotal_discount: number;
  subtotal_afterdiscount: number;
}

export function calculateLineAmounts(input: LineAmountInput): LineAmounts {
  const listPrice = Number(input.listPrice) || 0;
  const contractPrice = Number(input.contractPrice) || 0;
  const quantity = Number(input.quantity) || 0;
  const unit = input.unit;
  const asMarkup = Boolean(input.forceMarkup) || isMarkupRate({
    unit,
    chargeType: input.chargeType
  });
  // 採購無牌價：小計與折扣後皆為實際金額 × 收費比例，折扣為 0
  const billedRate = asMarkup ? contractPrice : listPrice;
  const subtotal = applyQuantity(billedRate, quantity, unit, asMarkup);
  const after = applyQuantity(contractPrice, quantity, unit, asMarkup);
  const discount = subtotal - after;
  if (input.isDeduction) {
    return {
      subtotal: -Math.abs(subtotal),
      subtotal_discount: -Math.abs(discount),
      subtotal_afterdiscount: -Math.abs(after)
    };
  }
  return {
    subtotal,
    subtotal_discount: discount,
    subtotal_afterdiscount: after
  };
}

export interface QuoteLineDisplay {
  isMarkup: boolean;
  /** 牌價欄：工時為單價；採購無牌價（與合約價金額相同，僅供相容） */
  listPriceAmount: number;
  /** 合約價欄：採購項目為實際採購金額 × 收費比例；工時項目為單價 */
  contractPriceAmount: number;
  /** 數量欄：倍率項目固定 1；工時項目為實際數量 */
  quantityDisplay: number;
  quantityUnitDisplay: string;
  /** 儲存／輸入用：倍率項目為實際採購金額 */
  purchaseAmount: number;
  listRatePercent: number;
  contractRatePercent: number;
}

/**
 * 報價列的顯示值。計算與儲存仍用實際採購金額當 quantity；
 * 倍率項目對外顯示數量 1、合約價為實際採購金額 × 收費比例（無獨立牌價）。
 */
export function getQuoteLineDisplay(input: LineAmountInput): QuoteLineDisplay {
  const amounts = calculateLineAmounts(input);
  const asMarkup = Boolean(input.forceMarkup) || isMarkupRate({
    unit: input.unit,
    chargeType: input.chargeType
  });
  if (asMarkup) {
    const percentUnit = isMarkupRateUnit(input.unit) ? input.unit : '%';
    const purchaseAmount = Number(input.quantity) || 0;
    const contractRatePercent = storedRateToPercent(input.contractPrice, percentUnit);
    return {
      isMarkup: true,
      listPriceAmount: amounts.subtotal,
      contractPriceAmount: purchaseContractAmount(purchaseAmount, contractRatePercent),
      quantityDisplay: MARKUP_DISPLAY_QUANTITY,
      quantityUnitDisplay: MARKUP_DISPLAY_UNIT,
      purchaseAmount,
      listRatePercent: contractRatePercent,
      contractRatePercent
    };
  }
  return {
    isMarkup: false,
    listPriceAmount: Number(input.listPrice) || 0,
    contractPriceAmount: Number(input.contractPrice) || 0,
    quantityDisplay: Number(input.quantity) || 0,
    quantityUnitDisplay: quantityUnitLabel(input.unit),
    purchaseAmount: Number(input.quantity) || 0,
    listRatePercent: 0,
    contractRatePercent: 0
  };
}

export function canonicalizeMarkupPrices(
  listPrice: number,
  contractPrice: number,
  listUnit?: string | null,
  contractUnit?: string | null
): { listPrice: number; contractPrice: number; unit: string; isMarkup: boolean } {
  const isMarkup = isMarkupRateUnit(listUnit) || isMarkupRateUnit(contractUnit);
  if (!isMarkup) {
    return {
      listPrice,
      contractPrice,
      unit: (listUnit || contractUnit || '小時').trim() || '小時',
      isMarkup: false
    };
  }
  const listU = inferMarkupUnitForStored(listPrice, listUnit);
  const contractU = inferMarkupUnitForStored(contractPrice, contractUnit);
  return {
    listPrice: storedRateToPercent(listPrice, listU),
    contractPrice: storedRateToPercent(contractPrice, contractU),
    unit: '%',
    isMarkup: true
  };
}

/**
 * 套用合約價：固定單價時合約價不得高於牌價；倍率計價允許 110% 高於牌價 100%。
 */
export function resolveQuoteItemRates(params: {
  listPrice: number;
  listUnit?: string | null;
  contractPrice?: number | null;
  contractUnit?: string | null;
  hasContractMatch: boolean;
}): { listPrice: number; contractPrice: number; unit: string } {
  const listPrice = Number(params.listPrice) || 0;
  const rawContract = params.contractPrice == null ? listPrice : Number(params.contractPrice) || 0;
  const canonical = canonicalizeMarkupPrices(
    listPrice,
    params.hasContractMatch ? rawContract : listPrice,
    params.listUnit,
    params.hasContractMatch ? params.contractUnit : params.listUnit
  );
  if (canonical.isMarkup) {
    return {
      listPrice: canonical.listPrice,
      contractPrice: params.hasContractMatch ? canonical.contractPrice : canonical.listPrice,
      unit: '%'
    };
  }
  let contractPrice = listPrice;
  if (params.hasContractMatch && params.contractPrice != null) {
    contractPrice = listPrice > 0 ? Math.min(rawContract, listPrice) : rawContract;
  }
  return {
    listPrice,
    contractPrice,
    unit: canonical.unit
  };
}

/** 合約服務項目寫入前：實報實銷／% 一律存成百分比數字 + 單位 `%` */
export function normalizeContractServiceRate(
  stored: number,
  unit?: string | null
): { price: number; unit: string } {
  if (isMarkupRateUnit(unit)) {
    return { price: storedRateToPercent(stored, unit), unit: '%' };
  }
  return { price: Number(stored) || 0, unit: (unit || '小時').trim() || '小時' };
}
